import { ThirtyDayStatistics } from '@lune-fe/dapi-models'
import { formatNumbers } from '@lune-fe/lune-components-lib'
import { LuneThemeNew, Text } from '@lune-fe/lune-ui-lib'
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined'
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined'
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined'
import Box from '@mui/material/Box'
import { FC } from 'react'

import EmptyStatePlaceholder from 'components/EmptyStatePlaceholder'
import Chart from 'images/chart.png'

const { palette } = LuneThemeNew

const sourceConfig: Record<
    ThirtyDayStatistics['sources'][number]['type'],
    { label: string; icon: JSX.Element; color: string }
> = {
    api: {
        label: 'API',
        icon: <CodeOutlinedIcon />,
        color: palette.dataColors.Data1100,
    },
    csv: {
        label: 'CSV',
        icon: <TableRowsOutlinedIcon />,
        color: palette.dataColors.Data200,
    },
    tender: {
        label: 'Tender',
        icon: <TableChartOutlinedIcon />,
        color: palette.dataColors.Data600,
    },
    widget: {
        label: 'Widget',
        icon: <WidgetsOutlinedIcon />,
        color: palette.dataColors.Data1700,
    },
    dashboard: {
        label: 'Dashboard',
        icon: <DesktopMacOutlinedIcon />,
        color: palette.Surface700,
    },
    chain_io: {
        label: 'Chain.io',
        icon: <WidgetsOutlinedIcon />,
        color: palette.dataColors.Data500,
    },
    ecotransit_csv: {
        label: 'EcoTransIT CSV',
        icon: <TableChartOutlinedIcon />,
        color: palette.dataColors.Data400,
    },
}

const CalculationSources: FC<{ data: ThirtyDayStatistics['sources'] | undefined }> = ({ data }) => {
    if (!data?.length) {
        return (
            <EmptyStatePlaceholder
                illustration={Chart}
                title={'No data to display'}
                description={'Run a few calculations to see a breakdown of your sources'}
            />
        )
    }

    const filteredAndSortedData = data
        .filter((source) => source.count > 0)
        .sort((a, b) => b.count - a.count)
    const totalCount = filteredAndSortedData.reduce((sum, source) => sum + source.count, 0)

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}
        >
            {filteredAndSortedData.map((source) => {
                const config = sourceConfig[source.type]
                const percentage =
                    totalCount > 0 ? Math.round((source.count / totalCount) * 100) : 0

                return (
                    <Box
                        key={source.type}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            width: '100%',
                            height: '48px',
                            lineHeight: '48px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                minWidth: '140px',
                            }}
                        >
                            <Box
                                sx={{
                                    color: palette.TextSecondary,
                                    display: 'flex',
                                    alignItems: 'center',
                                    svg: {
                                        fontSize: '20px',
                                    },
                                }}
                            >
                                {config.icon}
                            </Box>
                            <Text variant={'body3-medium'}>{config.label}</Text>
                        </Box>
                        <Box sx={{ minWidth: '60px', textAlign: 'right' }}>
                            <Text variant={'body3'}>{formatNumbers(source.count, 2)}</Text>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    height: '12px',
                                    width: `${percentage}%`,
                                    backgroundColor: config.color,
                                    minWidth: '2px',
                                }}
                            />
                            <Text
                                variant={'body3'}
                                sx={{ color: palette.TextSecondary, minWidth: '35px' }}
                            >
                                {percentage}%
                            </Text>
                        </Box>
                    </Box>
                )
            })}
        </Box>
    )
}

export default CalculationSources
